<template>
  <div class="ship-sheet-ov">
    <div ref="sheet"
         class="ship-sheet"
         role="dialog"
         aria-modal="true"
         aria-labelledby="tpl-sheet-title"
         tabindex="-1">
      <button type="button"
              class="btn btn-default ship-sheet-x"
              :aria-label="$t('messenger.ship.sheet.close')"
              @click="close">
        <i class="fal fa-times"></i>
      </button>

      <div class="ship-sheet-body">
        <Spinner v-if="tplState === 'loading'"
                 size="sm" />

        <div v-else-if="tplState === 'error'"
             class="alert alert-warning d-flex align-items-center flex-wrap mb-0 unavailable">
          <span><i class="fal fa-exclamation-triangle mr-1"></i>{{ $t("messenger.unavailable") }}</span>
          <button type="button"
                  class="btn btn-outline-primary btn-sm ml-auto"
                  @click="loadTemplate()">{{ $t("messenger.retry") }}</button>
        </div>

        <div v-else>
          <div class="card-title-row">
            <div>
              <h5 id="tpl-sheet-title"
                  class="header-text text-color-4">{{ $t("messenger.ship.sheet.title") }}</h5>
              <p class="muted-note mt-1">{{ pageLine }}</p>
            </div>
          </div>

          <div v-if="review === 'PENDING'"
               class="tpl-status --warn">
            <span class="badge badge-pill status-badge pill --warning"><i class="fal fa-hourglass-half"></i>{{ $t("messenger.ship.sheet.pending_title") }}</span>
            <p class="tpl-status-bold">{{ $t("messenger.ship.sheet.pending_body") }}</p>
            <p class="tpl-status-live">{{ $t("messenger.ship.sheet.pending_live", { live: liveLabel }) }}</p>
            <div class="tpl-status-pv">
              <span class="tpl-status-tag">{{ $t("messenger.ship.sheet.pending_tag") }}</span>
              <div class="mp-bubble">
                <div class="mp-text"
                     v-html="pendingHtml"></div>
              </div>
            </div>
            <div class="sys-actions">
              <Button variant="primary"
                      size="sm"
                      :loading="checking"
                      @click="checkStatus">{{ $t("messenger.ship.sheet.check") }}</Button>
            </div>
          </div>

          <div v-else-if="review === 'REJECTED'"
               class="tpl-status --bad">
            <span class="badge badge-pill status-badge pill --danger"><i class="fal fa-times-circle"></i>{{ $t("messenger.ship.sheet.rejected_title") }}</span>
            <p class="tpl-status-bold">{{ $t("messenger.ship.sheet.rejected_body", { live: liveLabel }) }}</p>
            <p v-if="rejectReason"
               class="tpl-status-reason">{{ rejectReason }}</p>
          </div>

          <div v-if="!connected"
               class="alert alert-danger d-flex align-items-center flex-wrap page-off">
            <i class="fal fa-exclamation-triangle"></i>
            <span>{{ $t("messenger.ship.sheet.page_off") }}</span>
            <Button variant="primary"
                    size="sm"
                    custom_class="ml-auto"
                    @click="$emit('reconnect')">
              <i class="fab fa-facebook mr-1"></i>{{ $t("messenger.ship.reconnect") }}
            </Button>
          </div>

          <div class="preview-pane mb-3">
            <div class="preview-head">
              <span class="t">{{ isNew ? $t("messenger.ship.sheet.new_not_live") : $t("messenger.ship.sheet.live_now") }}</span>
              <span v-if="statePill"
                    class="badge badge-pill status-badge pill --muted">
                <i v-if="statePill.icon"
                   :class="statePill.icon"></i>{{ statePill.label }}
              </span>
            </div>
            <div class="mp">
              <div class="mp-from">{{ page.name }}</div>
              <div class="tpl-pv-label">{{ $t("messenger.ship.sheet.cod_label") }}</div>
              <div class="mp-row">
                <PageAvatar :page="page"
                            size="sm" />
                <div class="mp-bubble">
                  <div class="mp-text"
                       v-html="previewCodHtml"></div>
                  <span class="mp-btn">{{ $t("messenger.ship.examples.track_button") }}</span>
                </div>
              </div>
              <div class="tpl-pv-label">{{ $t("messenger.ship.sheet.paid_label") }}</div>
              <div class="mp-row">
                <PageAvatar :page="page"
                            size="sm" />
                <div class="mp-bubble">
                  <div class="mp-text"
                       v-html="previewPaidHtml"></div>
                  <span class="mp-btn">{{ $t("messenger.ship.examples.track_button") }}</span>
                </div>
              </div>
              <div class="tpl-meta-note">{{ $t("messenger.ship.sheet.saved_note") }}</div>
            </div>
          </div>

          <div class="setting-block pt-0">
            <label class="setting-label"
                   for="tpl-body">{{ $t("messenger.ship.sheet.custom_label") }}</label>
            <div class="tpl-chips">
              <button v-for="chip in chips"
                      :key="chip.key"
                      type="button"
                      class="tpl-chip"
                      :class="['--' + chip.key, chip.have ? '--have' : '']"
                      @click="insertToken(chip.token)">
                <span class="t">{{ chip.token }}</span><span v-if="chip.state"
                                                             class="st">{{ chip.state }}</span>
              </button>
            </div>
            <div class="tpl-editor">
              <div ref="mirror"
                   class="tpl-mirror"
                   aria-hidden="true"
                   v-html="mirrorText"></div>
              <textarea id="tpl-body"
                        ref="ta"
                        v-model="draft"
                        class="form-control"
                        rows="6"
                        spellcheck="false"
                        autocomplete="off"
                        autocapitalize="off"
                        @scroll="syncScroll"></textarea>
            </div>
            <div class="tpl-meta">
              <span :class="{ 'text-warning font-weight-bold': tooLong || draft.length >= maxBody, '--bad': !!problem }">{{ problem }}</span>
              <span :class="{ 'text-warning font-weight-bold': tooLong || draft.length >= maxBody }">({{ draft.length }}/{{ maxBody }})</span>
            </div>
            <div v-if="saveError"
                 class="save-error">
              <i class="fal fa-exclamation-circle mr-1"></i>{{ saveError }}
            </div>
            <div class="sys-actions">
              <Button variant="primary"
                      size="sm"
                      :loading="saving"
                      :disabled="!canSave"
                      @click="save">{{ $t("messenger.ship.sheet.save") }}</Button>
              <button type="button"
                      class="btn btn-outline-primary btn-sm"
                      :disabled="saving || !canReset"
                      @click="resetDefault">{{ $t("messenger.ship.sheet.reset") }}</button>
            </div>
          </div>

          <div class="setting-block">
            <div class="test-row">
              <input v-model="testPhone"
                     type="tel"
                     inputmode="tel"
                     class="form-control"
                     :placeholder="$t('messenger.ship.sheet.test_phone')"
                     :aria-label="$t('messenger.ship.sheet.test_phone')"
                     :disabled="!connected" />
              <input v-model="testCod"
                     type="text"
                     inputmode="numeric"
                     class="form-control"
                     :placeholder="$t('messenger.ship.sheet.test_cod')"
                     :aria-label="$t('messenger.ship.sheet.test_cod')"
                     :disabled="!connected" />
              <Button variant="outline-primary"
                      size="sm"
                      :loading="testing"
                      :disabled="!connected || !testPhone.trim() || testing"
                      @click="sendTest">
                <i v-if="!testing"
                   class="fal fa-paper-plane mr-1"></i>{{ $t("messenger.ship.sheet.test_button") }}
              </Button>
            </div>
            <div class="hint">{{ $t("messenger.ship.sheet.test_hint") }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "@/components/core/Button";
import Spinner from "@/components/core/Spinner";
import PageAvatar from "@/components/derived/messenger/PageAvatar";
import { isPageConnected } from "@/store/modules/messenger";
import {
  TRACKING_TOKEN,
  COD_TOKEN,
  NAME_TOKEN,
  MAX_TEMPLATE_BODY,
  TEMPLATE_COUNT_FROM,
  toDisplayBody,
  toApiBody,
  mirrorHtml,
  previewHtml,
  DEFAULT_COD_BODY,
  bodyProblem,
  templateReviewStatus,
  templateRejectReason,
} from "@/components/derived/messenger/format";
import { Toast } from "@/helpers/toastr";

// ดูข้อความ: previews and the custom-copy editor for one Page.
// The editor loads custom.body when present, else the standard COD wording; {body: null} restores the default.
export default {
  name: "TrackingMessageSheet",
  components: { Button, Spinner, PageAvatar },
  props: {
    page: { type: Object, required: true },
  },

  data() {
    return {
      tplState: "loading", // loading | ready | error
      template: null,
      draft: "",
      saving: false,
      saveError: null,
      checking: false,
      testPhone: "",
      testCod: "",
      testing: false,
      maxBody: MAX_TEMPLATE_BODY,
    };
  },

  mounted() {
    this.loadTemplate();
    document.body.classList.add("--sheet-open");
    document.addEventListener("keydown", this.onKey);
    this.$nextTick(() => {
      if (this.$refs.sheet) this.$refs.sheet.focus();
    });
  },

  beforeDestroy() {
    document.body.classList.remove("--sheet-open");
    document.removeEventListener("keydown", this.onKey);
  },

  computed: {
    connected() {
      return isPageConnected(this.page);
    },

    custom() {
      return this.template && this.template.custom ? this.template.custom : null;
    },

    // What the editor loaded: the custom wording when the Page has one, else the standard COD wording
    baseDisplay() {
      if (this.custom && this.custom.body != null) return toDisplayBody(this.custom.body);
      return DEFAULT_COD_BODY;
    },

    review() {
      return templateReviewStatus(this.template);
    },

    rejectReason() {
      return templateRejectReason(this.template);
    },

    liveLabel() {
      const liveCustom = this.custom && this.custom.body && this.custom.active;
      return this.$t(liveCustom ? "messenger.ship.sheet.live_custom" : "messenger.ship.sheet.live_default");
    },

    dirty() {
      return !!this.template && this.draft !== this.baseDisplay;
    },

    isNew() {
      return this.review === "PENDING" || this.review === "REJECTED" || this.dirty;
    },

    pageLine() {
      const line = this.$t("messenger.ship.sheet.page_line", { name: this.page.name });
      return this.isNew ? line : line + " · " + this.$t("messenger.ship.sheet.live_now");
    },

    statePill() {
      if (!this.template) return null;
      if (this.dirty) return { icon: "fal fa-edit", label: this.$t("messenger.ship.sheet.unsaved") };
      if (this.review === "PENDING" || this.review === "REJECTED") return null;
      const liveCustom = this.custom && this.custom.body && this.custom.active;
      return { icon: null, label: this.$t(liveCustom ? "messenger.ship.sheet.saved_custom" : "messenger.ship.sheet.saved_standard") };
    },

    pendingHtml() {
      const body = this.custom && this.custom.body != null ? toDisplayBody(this.custom.body) : DEFAULT_COD_BODY;
      return mirrorHtml(body);
    },

    previewCodHtml() {
      return previewHtml(this.draft, false);
    },

    previewPaidHtml() {
      return previewHtml(this.draft, true);
    },

    mirrorText() {
      return mirrorHtml(this.draft);
    },

    chips() {
      const required = this.$t("messenger.ship.sheet.required");
      const defs = [
        { key: "track", token: TRACKING_TOKEN, must: true },
        { key: "cod", token: COD_TOKEN, must: true },
        { key: "name", token: NAME_TOKEN, must: false },
      ];
      return defs.map((def) => {
        const have = this.draft.indexOf(def.token) !== -1;
        return { key: def.key, token: def.token, have, state: have ? "✓" : def.must ? required : "" };
      });
    },

    apiBody() {
      return toApiBody(this.draft);
    },

    tooLong() {
      return this.apiBody.length > MAX_TEMPLATE_BODY;
    },

    showCounter() {
      return this.draft.length > TEMPLATE_COUNT_FROM;
    },

    problem() {
      const key = bodyProblem(this.apiBody);
      if (!key) return "";
      return this.$t("messenger.ship.sheet." + key, {
        track: TRACKING_TOKEN,
        cod: COD_TOKEN,
        name: NAME_TOKEN,
        max: MAX_TEMPLATE_BODY,
        slot: "{{…}}",
      });
    },

    canSave() {
      return !!this.template && this.dirty && !this.problem && !this.saving && this.connected;
    },

    canReset() {
      return !!(this.custom && this.custom.body);
    },
  },

  methods: {
    close() {
      this.$emit("close");
    },

    onKey(event) {
      if (event && event.key === "Escape") this.close();
    },

    syncScroll() {
      const ta = this.$refs.ta;
      const mirror = this.$refs.mirror;
      if (ta && mirror) {
        mirror.scrollTop = ta.scrollTop;
        mirror.scrollLeft = ta.scrollLeft;
      }
    },

    insertToken(token) {
      const ta = this.$refs.ta;
      if (!ta) {
        this.draft = this.draft + token;
        return;
      }
      const start = ta.selectionStart != null ? ta.selectionStart : this.draft.length;
      const end = ta.selectionEnd != null ? ta.selectionEnd : start;
      this.draft = this.draft.slice(0, start) + token + this.draft.slice(end);
      this.saveError = null;
      this.$nextTick(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = start + token.length;
        this.syncScroll();
      });
    },

    loadTemplate() {
      this.tplState = "loading";
      this.saveError = null;
      this.$store.dispatch("messenger/fetchTemplate", {
        pageId: this.page.pageId,
        callback: (ok, data) => {
          if (this._isDestroyed) return;
          if (!ok || !data || !data.shipped) {
            this.tplState = "error";
            return;
          }
          this.template = data;
          this.draft = this.baseDisplay;
          this.tplState = "ready";
          this.$nextTick(this.syncScroll);
        },
      });
    },

    checkStatus() {
      if (this.checking) return;
      this.checking = true;
      this.$store.dispatch("messenger/fetchTemplate", {
        pageId: this.page.pageId,
        force: true,
        callback: (ok, data) => {
          this.checking = false;
          if (this._isDestroyed) return;
          if (ok && data && data.shipped) {
            const editing = this.dirty;
            this.template = data;
            if (!editing) this.draft = this.baseDisplay;
          } else {
            Toast(this.$t("messenger.pages.action_failed"), "danger");
          }
        },
      });
    },

    // PUT {body: null} with no client validation, then read the Page's template again
    resetDefault() {
      if (!this.canReset || this.saving) return;
      this.saving = true;
      this.saveError = null;
      this.$store.dispatch("messenger/saveTemplate", {
        pageId: this.page.pageId,
        body: null,
        callback: (ok, result) => {
          if (this._isDestroyed) return;
          if (!ok) {
            this.saving = false;
            this.handleSaveError(result || {});
            return;
          }
          this.$store.dispatch("messenger/fetchTemplate", {
            pageId: this.page.pageId,
            force: true,
            callback: (fetched, data) => {
              this.saving = false;
              if (this._isDestroyed) return;
              if (!fetched || !data || !data.shipped) {
                Toast(this.$t("messenger.pages.action_failed"), "danger");
                return;
              }
              this.template = data;
              this.draft = this.baseDisplay;
              Toast(this.$t("messenger.tracking.saved"), "success");
              this.$emit("saved");
            },
          });
        },
      });
    },

    save() {
      if (!this.canSave) return;
      this.saving = true;
      this.saveError = null;
      this.$store.dispatch("messenger/saveTemplate", {
        pageId: this.page.pageId,
        body: this.apiBody,
        callback: (ok, result) => {
          this.saving = false;
          if (this._isDestroyed) return;
          if (ok && result && result.shipped) {
            // The PUT answers {shipped, custom} only: merge, keeping defaultBody and placeholders
            this.template = Object.assign({}, this.template, { shipped: result.shipped, custom: result.custom });
            this.draft = this.baseDisplay;
            Toast(
              this.$t(templateReviewStatus(this.template) === "PENDING" ? "messenger.tracking.saved_pending" : "messenger.tracking.saved"),
              "success"
            );
            this.$emit("saved");
            return;
          }
          if (ok) {
            this.loadTemplate();
            return;
          }
          this.handleSaveError(result || {});
        },
      });
    },

    // Never shows the backend's raw detail: only the code decides the words
    handleSaveError(err) {
      if (err.code === "invalid_body") {
        this.saveError = this.$t("messenger.ship.sheet.invalid_body");
        return;
      }
      if (err.code === "migration_pending") {
        this.saveError = this.$t("messenger.ship.sheet.updating");
        return;
      }
      if (err.code === "meta_rejected") {
        this.saveError = this.$t("messenger.tracking.meta_rejected");
        return;
      }
      if (err.code === "page_not_connected") {
        Toast(this.$t("messenger.ship.sheet.page_off"), "danger");
        return;
      }
      this.saveError = this.$t("messenger.tracking.save_failed");
    },

    sendTest() {
      const phone = this.testPhone.trim();
      if (!phone || this.testing || !this.connected) return;
      this.testing = true;
      this.$store.dispatch("messenger/sendTestMessage", {
        pageId: this.page.pageId,
        phone,
        codAmount: String(this.testCod || "").replace(/[,\s]/g, ""),
        callback: (ok, err) => {
          this.testing = false;
          if (this._isDestroyed) return;
          if (ok) {
            Toast(this.$t("messenger.tracking.test_sent"), "success");
            return;
          }
          const key =
            {
              no_contact: "test_no_contact",
              invalid_phone: "test_invalid_phone",
              page_not_connected: "page_off",
              template_pending: "test_pending",
              rate_limited: "test_rate_limited",
            }[err && err.code] || "test_failed";
          Toast(this.$t("messenger.tracking." + key), "danger");
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ship-sheet .btn {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ship-sheet-ov {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

@media (min-width: 768px) {
  .ship-sheet-ov {
    align-items: center;
    padding: 2rem;
  }
}

.ship-sheet {
  position: relative;
  width: 100%;
  max-width: 720px;
  max-height: 88vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 16px 16px 0 0;
}

@media (min-width: 768px) {
  .ship-sheet {
    border-radius: 16px;
  }
}

.ship-sheet-x {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  z-index: 2;
  min-width: 44px;
}

.ship-sheet-body {
  padding: 3.5rem 1.25rem 1.25rem;
}

.card-title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;

  h5 {
    margin: 0;
  }
}

.muted-note {
  color: map-get($text-colors, 5);
  font-size: 0.875rem;
}

.unavailable {
  gap: 0.5rem;
}

.page-off {
  gap: 0.5rem;
}

.status-badge {
  &.--success {
    background-color: rgba($brand-green-mid, 0.1);
    color: $brand-green-mid;
  }

  &.--danger {
    background-color: rgba($brand-pink, 0.1);
    color: $brand-pink;
  }

  &.--warning {
    background-color: rgba(255, 152, 0, 0.1);
    color: #e68900;
  }

  &.--muted {
    background-color: rgba(158, 158, 158, 0.1);
    color: #8a8a8a;
  }
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.75rem;
  border-radius: 10rem;
  font-size: 0.8125rem;
  font-weight: 700;
  white-space: nowrap;
}

.tpl-status {
  border-radius: 10px;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;

  &.--warn {
    background: rgba(255, 152, 0, 0.1);
  }

  &.--bad {
    background: rgba(246, 116, 116, 0.1);
  }

  .pill {
    margin-bottom: 0.5rem;
  }
}

.tpl-status-bold {
  font-weight: 700;
  margin: 0 0 0.25rem;
}

.tpl-status-live {
  margin: 0 0 0.65rem;
  color: #5f6368;
}

.tpl-status-reason {
  margin: 0;
  color: #5f6368;
}

.tpl-status-pv {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.75rem;
}

.tpl-status-tag {
  font-size: 0.75rem;
  font-weight: 700;
  color: #9a5a00;
}

.ship-sheet .tpl-status .mp-bubble {
  font-size: 0.875rem;
  padding: 0;
  max-width: 100%;
  background: #fff;
  border: 1px solid #e4e6eb;
}

.ship-sheet .tpl-status .mp-text {
  font-size: 0.875rem;
}

.preview-pane {
  background: $dashboard-background;
  border-radius: 10px;
  padding: 1rem;
}

.preview-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;

  .t {
    color: #646464;
    font-size: 0.9375rem;
  }
}

.mp {
  background: #fff;
  border-radius: 10px;
  padding: 0.9rem 0.9rem 0.8rem;
}

.mp-from {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #aaa;
  font-size: 0.75rem;
  margin: 0 0 0.35rem 36px;
}

.mp-row {
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.mp-bubble {
  background: #f0f0f0;
  border-radius: 18px;
  max-width: 290px;
  overflow: hidden;
  color: #1c1e21;
  min-width: 0;
}

.mp-text {
  padding: 0.6rem 0.9rem;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9375rem;
  line-height: 1.45;
}

.mp-btn {
  display: block;
  border-top: 1px solid #dcdcdc;
  padding: 0.55rem 0.9rem;
  text-align: center;
  color: #1c75bb;
  font-weight: 700;
  font-size: 0.9375rem;
}

.tpl-pv-label {
  color: #5f6368;
  font-size: 0.75rem;
  margin: 0.55rem 0 0.3rem 36px;
}

.mp-from + .tpl-pv-label {
  margin-top: 0;
}

.tpl-meta-note {
  margin: 0.6rem 0 0 36px;
  font-size: 0.8125rem;
  color: #5f6368;
}

.setting-block {
  padding: 1rem 0;
  border-bottom: 1px solid $dashboard-background;

  &:last-child {
    border-bottom: 0;
  }

  .hint {
    color: map-get($text-colors, 5);
    font-size: 0.8125rem;
    margin-top: 0.3rem;
  }
}

.setting-label {
  color: map-get($text-colors, 3);
  font-size: 1rem;
  margin-bottom: 0.5rem;
  display: block;
}

.ph {
  border-radius: 3px;
  white-space: nowrap;
}

.ph.--track {
  color: #1c75bb;
  background: #f0f8ff;
  font-weight: 700;
}

.ph.--cod {
  color: #9a5a00;
  background: #fdf0d5;
  font-weight: 700;
}

.ph.--name {
  color: #5f6368;
  background: #e8e8e8;
}

.mp-text .ph {
  padding: 0 2px;
}

.tpl-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}

.tpl-chip {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.85rem;
  border-radius: 10rem;
  border: 1px solid;
  font-family: inherit;
  font-size: 0.875rem;
  line-height: 1.2;
  cursor: pointer;

  &.--track {
    color: #1c75bb;
    background: #f0f8ff;
    border-color: #b9d9f2;
    font-weight: 700;
  }

  &.--cod {
    color: #9a5a00;
    background: #fdf0d5;
    border-color: #ecd9a8;
    font-weight: 700;
  }

  &.--name {
    color: #5f6368;
    background: #f1f1f1;
    border-color: #d5d5d5;
  }

  .st {
    font-size: 0.75rem;
    font-weight: 700;
    opacity: 0.85;
  }
}

.tpl-chip:focus-visible {
  outline: 2px solid #1c75bb;
  outline-offset: 2px;
}

.tpl-editor {
  position: relative;
}

.tpl-mirror,
#tpl-body {
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.6;
}

.tpl-mirror {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  padding: 0.375rem 0.75rem;
  color: #495057;
  background: #fff;
  white-space: pre-wrap;
  word-break: break-word;
}

#tpl-body {
  position: relative;
  z-index: 1;
  background-color: transparent;
  color: transparent;
  caret-color: #495057;
  padding: 0.375rem 0.75rem;
  height: auto;
  resize: vertical;
  line-height: 1.6;
}

#tpl-body::selection {
  background: rgba(28, 117, 187, 0.25);
}

.tpl-meta {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.25rem 0.75rem;
  margin-top: 0.4rem;
  font-size: 0.8125rem;
  color: #aaa;

  .--bad {
    color: #cf3a3a;
  }
}

.save-error {
  color: #cf3a3a;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

.sys-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
  align-items: center;

  .btn {
    min-width: 88px;
  }
}

.test-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;

  .form-control {
    flex: 1 1 10rem;
    min-height: 44px;
    min-width: 0;
  }
}

body:not(.--dark) {
  .status-badge.--success {
    color: #1a6e2e;
  }

  .status-badge.--warning {
    color: #9a5a00;
  }

  .status-badge.--muted {
    color: #666;
  }

  .status-badge.--danger {
    color: #c62828;
  }

  .muted-note {
    color: #767676;
  }

  .mp-from,
  .hint {
    color: #767676;
  }

  .mp-btn {
    color: #155f99;
  }
}

body.--dark {
  .ship-sheet {
    background: #373737;
  }

  .ship-sheet .btn-outline-primary {
    color: #8cc8f5;
    border-color: #8cc8f5;
  }

  .ship-sheet .btn-outline-primary:hover:not(:disabled) {
    color: #fff;
  }

  .preview-pane {
    background: #4b4b4b;
  }

  .preview-head .t {
    color: #bbb;
  }

  .mp {
    background: #373737;
  }

  .mp-bubble {
    background: #4b4b4b;
    color: lightgray;
  }

  .mp-btn {
    color: #8cc8f5;
    border-top-color: #666;
  }

  .tpl-pv-label,
  .tpl-meta-note {
    color: #c4c4c4;
  }

  .ph.--track {
    color: #9fd2f8;
    background: #12334d;
  }

  .ph.--cod {
    color: #ffd98a;
    background: #4a3200;
  }

  .ph.--name {
    color: #e0e0e0;
    background: #555;
  }

  .tpl-mirror {
    color: lightgray;
    background: #373737;
  }

  #tpl-body {
    caret-color: lightgray;
  }

  .tpl-chip.--track {
    color: #9fd2f8;
    background: #12334d;
    border-color: #2c5a85;
  }

  .tpl-chip.--cod {
    color: #ffd98a;
    background: #4a3200;
    border-color: #7a5c14;
  }

  .tpl-chip.--name {
    color: #e0e0e0;
    background: #4b4b4b;
    border-color: #666;
  }

  .tpl-chip .t,
  .tpl-chip .st {
    color: inherit;
  }

  .tpl-chip:focus-visible {
    outline-color: #8cc8f5;
  }

  .tpl-meta .--bad,
  .save-error {
    color: #ff9d9d;
  }

  .tpl-status.--warn {
    background: rgba(255, 152, 0, 0.16);
  }

  .tpl-status.--bad {
    background: rgba(246, 116, 116, 0.16);
  }

  .tpl-status-live,
  .tpl-status-reason {
    color: #c4c4c4;
  }

  .tpl-status-tag {
    color: #ffd98a;
  }

  .ship-sheet .tpl-status .mp-bubble {
    background: #4b4b4b;
    border-color: #666;
    color: lightgray;
  }

  .ship-sheet .status-badge.--danger {
    color: #ff9d9d;
  }
}

@media (max-width: 767.98px) {
  .tpl-mirror,
  #tpl-body {
    font-size: 16px;
  }

  .pill {
    font-size: 12px;
  }

  .tpl-pv-label,
  .tpl-status-tag {
    font-size: 12px;
  }
}
</style>

<style>
body.--sheet-open {
  overflow: hidden;
}
</style>
